import React, {useState} from 'react';
import SelfAssessment from './SelfAssessment';
import PeerAssessment from './PeerAssessment';
import PreparatorySelfAssessment from './PreparatorySelfAssessment';
import PreparatoryPeerAssessment from './PreparatoryPeerAssessment';
import MiddleStageSelfAssessment from './MiddleStageSelfAssessment';
import MiddleStagePeerAssessment from './MiddleStagePeerAssessment';
import {isMiddleGrade, isPreparatoryGrade} from '../../utils/gradeUtils';

/**
 * A wrapper component that renders either the foundational, preparatory, or middle stage
 * assessment components based on the student's grade level
 */
const AssessmentWrapper = ({
                               selectedClass,
                               assignmentId,
                               submission,
                               // Form state and handlers for foundational components
                               selfAssessmentForm,
                               handleSelfAssessmentChange,
                               handleNeededChange,
                               handleSelfAssessmentSubmit,
                               peerAssessmentForm,
                               handlePeerAssessmentChange,
                               handleFriendNeededChange,
                               handlePeerAssessmentSubmit,
                               // Form state and handlers for middle stage components
                               middleSelfAssessmentForm,
                               handleMiddleSelfAssessmentChange,
                               middlePeerAssessmentForm,
                               handleMiddlePeerAssessmentChange,
                               // Callback for preparatory and middle stage components
                               onSelfAssessmentComplete,
                               onPeerAssessmentComplete
                           }) => {
    // State for loading indicators
    const [selfAssessmentLoading, setSelfAssessmentLoading] = useState(false);
    const [peerAssessmentLoading, setPeerAssessmentLoading] = useState(false);

    // Determine which grade level the student is in
    const grade = selectedClass?.grade ? parseInt(selectedClass.grade, 10) : 1;
    const isPreparatory = isPreparatoryGrade(grade);
    const isMiddle = isMiddleGrade(grade);

    // Render the appropriate assessment components based on grade level
    const renderSelfAssessment = () => {
        if (isMiddle) {
            return (
                <MiddleStageSelfAssessment
                    assignmentId={assignmentId}
                    submission={submission}
                    onSubmitComplete={onSelfAssessmentComplete}
                    loading={selfAssessmentLoading}
                    setLoading={setSelfAssessmentLoading}
                />
            );
        } else if (isPreparatory) {
            return (
                <PreparatorySelfAssessment
                    assignmentId={assignmentId}
                    submission={submission}
                    onSubmitComplete={onSelfAssessmentComplete}
                />
            );
        } else {
            return (
                <SelfAssessment
                    selfAssessmentForm={selfAssessmentForm}
                    handleSelfAssessmentChange={handleSelfAssessmentChange}
                    handleNeededChange={handleNeededChange}
                    handleSelfAssessmentSubmit={handleSelfAssessmentSubmit}
                    submission={submission}
                />
            );
        }
    };

    const renderPeerAssessment = () => {
        if (isMiddle) {
            return (
                <MiddleStagePeerAssessment
                    assignmentId={assignmentId}
                    submission={submission}
                    onSubmitComplete={onPeerAssessmentComplete}
                    loading={peerAssessmentLoading}
                    setLoading={setPeerAssessmentLoading}
                />
            );
        } else if (isPreparatory) {
            return (
                <PreparatoryPeerAssessment
                    assignmentId={assignmentId}
                    submission={submission}
                    onSubmitComplete={onPeerAssessmentComplete}
                />
            );
        } else {
            return (
                <PeerAssessment
                    peerAssessmentForm={peerAssessmentForm}
                    handlePeerAssessmentChange={handlePeerAssessmentChange}
                    handleFriendNeededChange={handleFriendNeededChange}
                    handlePeerAssessmentSubmit={handlePeerAssessmentSubmit}
                    submission={submission}
                />
            );
        }
    };

    return (
        <div className="space-y-6">
            {/* Render Self Assessment */}
            {renderSelfAssessment()}

            {/* Render Peer Assessment */}
            {renderPeerAssessment()}
        </div>
    );
};

export default AssessmentWrapper;